import CheckBoxField from '@/components/fields/CheckBoxField';
import { getPaymentTypeText } from '@/components/PatientPageV2/utils/getPaymentTypeText';
import {
  FF_AWAITING_DOCUMENT_DOCTOR_CONSULT_STATUS,
  FF_ENABLE_INITIAL_DR_PAYMENT,
  FF_ENABLE_SHOPIFY_PAYMENT,
} from '@/constants/featureFlags';
import { ConsultationStatus } from '@/data/constants';
import { getAuthData, getUser } from '@/data/service/authService';
import { useFeatureFlags } from '@/hooks';
import { useCanUserChargeConsult } from '@/hooks/consult/useCanUserChargeConsult';
import { useChargeInitialDoctorConsult } from '@/hooks/consult/useChargeInitialDoctorConsult';
import { useConsult } from '@/hooks/consult/useConsult';
import type { SaveConsultationFunc } from '@/hooks/consult/useSaveConsultation';
import type { ConsultationStatusMapped } from '@/hooks/rest/useGetConsultationStatus';
import type { OnSaveConsultationOptions } from '@/pages/Patient';
import { SectionHeader } from '@/shared-ui/PageElements/SectionHeader';
import { useAppStore } from '@/state-management';
import { useConsultationStore } from '@/state-management/slices/consultations/hooks/useConsultationStore';
import { Button, Typography } from '@montugroup/design-system';
import { Box, CircularProgress, Stack } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { ConsultationConfirmationButton } from '../../ConsultationConfirmationButton/ConsultationConfirmationButton';
import { PrescriptionPadStep } from '../../PrescriptionPad';
import type { ConsultationStatusSelectorProps } from '../ConsultationStatusSelector';
import { ConsultationStatusSelector } from '../ConsultationStatusSelector';
import { NoShowConfirmationModal } from '../NoShowConfirmationModal';
import type { ShowConsultation } from '../ShowConsultation';
import { StyledContainer, StyledContainerForHeader, StyledFooter } from './DoctorConsultActions.styles';
import {
  isCancelVisible,
  isNoShowVisible,
  isPaymentButtonDisabled,
  isResendInvoiceVisible,
  isShopifyPaymentVisible,
} from './DoctorConsultActions.utils';
import { PaymentInfoText } from './PaymentInfoText';
import { SecondaryAction } from './SecondaryAction';

interface DoctorConsultActionsProps
  extends Pick<ConsultationStatusSelectorProps, 'consultationStatuses' | 'handleSubStatusChange'> {
  showConsultation: ShowConsultation;
  patientActive: boolean;
  handleResendInvoice: () => Promise<void>;
  loadingPaymentAction: boolean;
  consultationStatuses: ConsultationStatusMapped[];
  handleCancelScript: () => void;
  handleNoShow: () => void;
  isComplimentary: boolean;
  handleIsComplimentaryChange: () => void;
  handleShopifyPayment: () => Promise<void>;
  handleSaveConsultation: (params?: OnSaveConsultationOptions) => Promise<void>;
  handleCompleteConsultation: () => void;
  isSavingConsultation?: boolean;
  handleSubmit: SaveConsultationFunc;
}

export const DoctorConsultActions = ({
  showConsultation,
  patientActive,
  isComplimentary,
  handleIsComplimentaryChange,
  handleResendInvoice,
  loadingPaymentAction,
  handleNoShow,
  handleCancelScript,
  handleSaveConsultation,
  handleShopifyPayment,
  handleCompleteConsultation,
  isSavingConsultation,
  consultationStatuses,
  handleSubStatusChange,
  handleSubmit,
}: DoctorConsultActionsProps) => {
  const user = getUser();
  const { isManager, isAdmin, isDoc } = getAuthData();
  const { flags } = useFeatureFlags();
  const ffEnableShopifyPayment = flags[FF_ENABLE_SHOPIFY_PAYMENT];
  const ffEnableInitialDrPayment = flags[FF_ENABLE_INITIAL_DR_PAYMENT];
  const ffAwaitingDocument = flags[FF_AWAITING_DOCUMENT_DOCTOR_CONSULT_STATUS];
  const [noShowModalOpen, setNoShowModalOpen] = useState(false);
  const [consultationStatusId, setConsultationStatusId] = useState(showConsultation.status_id);
  const {
    prescriptionPad: { step: createScriptStep, medicationPadItems },
    prescriptionHistory: { itemsToCancel },
  } = useAppStore.use.prescriptionAssistant();
  const onPrescriptionCreationStep = createScriptStep === PrescriptionPadStep.Create;
  const { paymentRequestType } = useConsultationStore(showConsultation);

  const paymentTypeText = getPaymentTypeText({ consultation: showConsultation, ffEnableShopifyPayment });

  const { paymentState, paymentMethod } = useConsult({
    paymentStatus: showConsultation?.payment_status,
    paymentDetails: showConsultation?.paymentStatus || {},
    paidDate: showConsultation?.paid_date ?? '',
    paymentQueued: showConsultation?.payment_queued,
    invoiceCode: showConsultation?.xero_consultation_code,
  });
  const { canUserChargeConsult } = useCanUserChargeConsult({
    consultationType: showConsultation?.consult_type,
    doctorId: showConsultation?.doctor_id,
  });

  const { shouldNotChargeInitialDoctorConsultation } = useChargeInitialDoctorConsult(
    showConsultation?.consult_type,
    showConsultation?.doctor_id,
    showConsultation?.hasPaidInitialNurseConsult,
  );

  const paymentButtonDisabled = isPaymentButtonDisabled({
    processingPayment: loadingPaymentAction,
    paymentState,
    paymentMethod,
  });

  const isChargeableDoctorConsult = isDoc && canUserChargeConsult;
  const hasBeenInvoicedByEway =
    showConsultation?.xero_consultation_code !== undefined && !showConsultation?.pricingTier;
  const isPaymentInProgressOrCompleted = paymentState !== 'NOT_PAID';

  // NOTE:  duplicate code from ConsultationConfirmationButton
  const validPrescriptionMedications = medicationPadItems.filter((item) => !!item.medicationPadItem);
  const showStatusSelector =
    ffAwaitingDocument && validPrescriptionMedications.length === 0 && itemsToCancel.length === 0;

  const showResendInvoiceButton = isResendInvoiceVisible({
    ffEnableShopifyPayment,
    isPaymentInProgressOrCompleted,
    hasBeenInvoicedByEway,
  });
  const showShopifyPaymentButton = isShopifyPaymentVisible({
    ffEnableShopifyPayment,
    paymentState,
    hasBeenInvoicedByEway,
  });
  const showNoShowButton = isNoShowVisible({
    isChargeableDoctorConsult,
    isPaymentInProgressOrCompleted,
    onPrescriptionCreationStep,
  });
  const showCancelButton = isCancelVisible({
    isChargeableDoctorConsult,
    isPaymentInProgressOrCompleted,
    onPrescriptionCreationStep,
    showStatusSelector,
    ffAwaitingDocument,
  });

  const setNoShow = () => {
    setNoShowModalOpen(true);
  };

  const updatedConsultationStatuses = useMemo(() => {
    if (!ffAwaitingDocument) {
      return [];
    }

    const SHOW_SELECTOR_OPTIONS: { [key: string]: boolean } = {
      [ConsultationStatus.no_show]: showNoShowButton,
      [ConsultationStatus.awaiting_document]: onPrescriptionCreationStep,
    };
    const filteredStatuses = (consultationStatuses ?? []).filter(
      (status) => SHOW_SELECTOR_OPTIONS[status.value.toString()] ?? false,
    );
    return filteredStatuses.length > 0
      ? [
          ...filteredStatuses,
          {
            name: 'Completed',
            value: ConsultationStatus.completed,
          },
        ]
      : [];
  }, [consultationStatuses, showNoShowButton, onPrescriptionCreationStep, ffAwaitingDocument]);

  useEffect(() => {
    if (updatedConsultationStatuses.length === 0) {
      return;
    }

    if (updatedConsultationStatuses.some(({ value }) => value === consultationStatusId)) {
      return;
    }
    setConsultationStatusId(ConsultationStatus.completed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultationStatusId, ffAwaitingDocument, updatedConsultationStatuses]);

  const handleInternalSubmit = () => {
    switch (consultationStatusId) {
      case ConsultationStatus.no_show:
        setNoShow();
        break;
      case ConsultationStatus.completed:
        handleCompleteConsultation();
        break;
      default:
        handleSubmit(
          {
            ...showConsultation,
            status_id: consultationStatusId,
            sub_status_id: showConsultation.sub_status_id ?? 0,
          },
          {
            askConfirmation: true,
            refetchData: true,
            closeConsultation: true,
            enableCustomOnSave: false,
          },
        );
        break;
    }
  };

  if (showConsultation.Doctor.DoctorUser.id !== user?.id && !isAdmin && !isManager) {
    return null;
  }

  const renderConsultationConfirmationButton = () =>
    showStatusSelector ? (
      <Stack direction="row" alignItems="stretch" spacing={4} data-testid="consultation-status-actions">
        <ConsultationStatusSelector
          statusId={consultationStatusId}
          subStatusId={showConsultation.sub_status_id}
          consultationStatuses={updatedConsultationStatuses}
          handleStatusChange={setConsultationStatusId}
          handleSubStatusChange={handleSubStatusChange}
          disabled={!patientActive}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleInternalSubmit}
          disabled={
            !patientActive || consultationStatusId === showConsultation.ConsultationStatus.id || isSavingConsultation
          }
          sx={{ borderRadius: 1, textTransform: 'none' }}
          startIcon={isSavingConsultation ? <CircularProgress size={16} /> : null}
        >
          Submit
        </Button>
      </Stack>
    ) : (
      <>
        {showNoShowButton && !ffAwaitingDocument && (
          <SecondaryAction onclick={setNoShow} disabled={!patientActive} label="No Show" color="primary" />
        )}
        <ConsultationConfirmationButton
          onClick={handleCompleteConsultation}
          isLoading={isSavingConsultation}
          isPatientActive={patientActive}
          paymentState={paymentState}
          selectedPaymentRequestType={paymentRequestType ?? null}
          shouldNotChargeInitialDoctorConsultation={shouldNotChargeInitialDoctorConsultation}
        />
      </>
    );

  const renderChargeableDoctorConsultActions = () => {
    return (
      <StyledContainerForHeader>
        {!ffEnableInitialDrPayment && (
          <>
            <SectionHeader headerText="Payment Details" />
            <Box>
              <PaymentInfoText consultPaymentState={paymentState} hasBeenInvoicedByEway={hasBeenInvoicedByEway} />
            </Box>
            <StyledFooter>
              <Box>
                {isPaymentInProgressOrCompleted ? (
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
                    <Typography variant="body2" fontWeight={600} display={'inline'}>
                      Payment Type:
                    </Typography>
                    <Typography variant="body2" display={'inline'}>
                      {paymentTypeText}
                    </Typography>
                  </Box>
                ) : (
                  <CheckBoxField
                    id="complimentary-consultation"
                    label="Complimentary consultation"
                    placeholder="-"
                    labelPadding="11px 7px 7px 0px"
                    labelFontWeight={900}
                    checked={isComplimentary}
                    onChange={handleIsComplimentaryChange}
                    customGrid={[11, 1]}
                    margin={0}
                    disabled={isPaymentInProgressOrCompleted}
                  />
                )}
              </Box>
            </StyledFooter>
          </>
        )}
        <Box display="flex" gap={2} flexDirection="column">
          <Box display="flex" gap={2} flexDirection="row" justifyContent="flex-end">
            {showResendInvoiceButton && (
              <SecondaryAction
                onclick={handleResendInvoice}
                disabled={paymentButtonDisabled}
                loading={loadingPaymentAction}
                label="Resend Invoice"
                color="primary"
              />
            )}
            {showShopifyPaymentButton && (
              <SecondaryAction
                onclick={() => {
                  handleShopifyPayment();
                }}
                disabled={paymentButtonDisabled}
                loading={loadingPaymentAction}
                label="Resend Payment Link"
                color="primary"
              />
            )}
          </Box>
        </Box>
      </StyledContainerForHeader>
    );
  };

  return (
    <>
      {isChargeableDoctorConsult && renderChargeableDoctorConsultActions()}

      <StyledContainer>
        <SectionHeader headerText="Actions" sx={{ marginBottom: '1rem' }} />
        <Stack direction="row" alignItems="stretch" justifyContent="space-between" spacing={4}>
          <SecondaryAction
            id="consultation-save-btn"
            onclick={() => handleSaveConsultation()}
            disabled={!patientActive}
            label="Save"
            color="primary"
          />

          <Stack direction="row" justifyContent="flex-end" spacing={4}>
            {showCancelButton && (
              <SecondaryAction onclick={handleCancelScript} disabled={!patientActive} label="Cancel" color="primary" />
            )}
            {renderConsultationConfirmationButton()}
          </Stack>
        </Stack>

        <NoShowConfirmationModal
          open={noShowModalOpen}
          onConfirm={() => {
            setNoShowModalOpen(false);
            handleNoShow();
          }}
          onClose={() => setNoShowModalOpen(false)}
        />
      </StyledContainer>
    </>
  );
};
